import { useState, useEffect, useCallback } from "react";
import { Cookie, X, ShieldCheck, BarChart3, Megaphone, Settings2, Check, ChevronRight } from "lucide-react";
import "./CookieConsent.css";

const STORAGE_KEY = "springup-cookie-consent";

const defaultConsent = {
  necessary: true,
  analytics: false,
  marketing: false,
  functional: false,
};

export const getCookieConsent = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [consent, setConsent] = useState(defaultConsent);
  const [initialLoaded, setInitialLoaded] = useState(false);

 useEffect(() => {
  const saved = getCookieConsent();
  if (saved) {
    setConsent(saved);
    document.body.classList.remove("cookie-consent-pending");
  } else {
    setVisible(true);
    document.body.classList.add("cookie-consent-pending");
  }
  setInitialLoaded(true);

  return () => {
    document.body.classList.remove("cookie-consent-pending");
  };
}, []);

const saveConsent = useCallback((next) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  setConsent(next);
  setVisible(false);
  setSettingsOpen(false);
  document.body.classList.remove("cookie-consent-pending"); // unlock navbar
}, []);

  const acceptAll = useCallback(() => {
    const next = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true,
    };
    saveConsent(next);
  }, [saveConsent]);

  const rejectAll = useCallback(() => {
    const next = { ...defaultConsent };
    saveConsent(next);
  }, [saveConsent]);

  const savePreferences = useCallback(() => {
    saveConsent(consent);
  }, [consent, saveConsent]);

  const toggle = useCallback((key) => {
    if (key === "necessary") return;
    setConsent((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  if (!initialLoaded) return null;

  return (
    <>
      {/* Banner */}
      <div
        className={`cookie-consent-banner ${visible ? "is-visible" : ""}`}
        role="dialog"
        aria-live="polite"
        aria-label="Cookie consent"
      >
        <div className="cookie-consent-backdrop" />
        <div className="cookie-consent-panel">
          <div className="cookie-consent-content">
            <div className="cookie-consent-icon">
              <Cookie size={22} />
            </div>
            <div className="cookie-consent-text">
              <h3 className="cookie-consent-title">We value your privacy</h3>
              <p className="cookie-consent-desc">
                We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. By clicking <strong>Accept All</strong>, you consent to our use of cookies.
              </p>
            </div>
          </div>
          <div className="cookie-consent-actions">
            <button className="cookie-consent-btn cookie-consent-btn-primary" onClick={acceptAll}>
              <Check size={16} /> Accept All
            </button>
            {/* <button className="cookie-consent-btn cookie-consent-btn-outline" onClick={rejectAll}>
              Reject All
            </button> */}
            {/* <button className="cookie-consent-btn cookie-consent-btn-ghost" onClick={() => setSettingsOpen(true)}>
              <Settings2 size={16} /> Cookie Settings
            </button> */}
          </div>
          {/* <button
            className="cookie-consent-close"
            onClick={() => setVisible(false)}
            aria-label="Close cookie banner"
            title="Close"
          >
            <X size={16} />
          </button> */}
        </div>
      </div>

      {/* Settings Modal */}
      <div
        className={`cookie-consent-modal ${settingsOpen ? "is-open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="Cookie settings"
      >
        <div className="cookie-consent-modal-overlay" onClick={() => setSettingsOpen(false)} />
        <div className="cookie-consent-modal-card">
          <div className="cookie-consent-modal-header">
            <div className="cookie-consent-modal-brand">
              <div className="cookie-consent-modal-icon">
                <Cookie size={20} />
              </div>
              <div>
                <h3 className="cookie-consent-modal-title">Cookie Preferences</h3>
                <p className="cookie-consent-modal-sub">Manage how we use cookies on your device.</p>
              </div>
            </div>
            <button
              className="cookie-consent-modal-close"
              onClick={() => setSettingsOpen(false)}
              aria-label="Close cookie settings"
            >
              <X size={18} />
            </button>
          </div>
          <div className="cookie-consent-modal-body">
            <p className="cookie-consent-intro">
              You can customize your cookie preferences below. Necessary cookies are always active because they are required for the website to function.
            </p>
            <div className="cookie-consent-options">
              <ConsentItem
                icon={<ShieldCheck size={18} />}
                title="Necessary"
                desc="Required for the website to work properly. Cannot be disabled."
                checked={consent.necessary}
                onToggle={() => toggle("necessary")}
                disabled
              />
              <ConsentItem
                icon={<BarChart3 size={18} />}
                title="Analytics"
                desc="Help us understand how visitors interact with our website."
                checked={consent.analytics}
                onToggle={() => toggle("analytics")}
              />
              <ConsentItem
                icon={<Megaphone size={18} />}
                title="Marketing"
                desc="Used to deliver relevant advertisements and marketing campaigns."
                checked={consent.marketing}
                onToggle={() => toggle("marketing")}
              />
              <ConsentItem
                icon={<Settings2 size={18} />}
                title="Functional"
                desc="Enable enhanced functionality and personalization, such as live chat."
                checked={consent.functional}
                onToggle={() => toggle("functional")}
              />
            </div>
          </div>
          <div className="cookie-consent-modal-footer">
            <button className="cookie-consent-btn cookie-consent-btn-text" onClick={rejectAll}>
              Reject All
            </button>
            <button className="cookie-consent-btn cookie-consent-btn-primary" onClick={savePreferences}>
              Save Preferences <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

const ConsentItem = ({ icon, title, desc, checked, onToggle, disabled }) => (
  <div className={`cookie-consent-option ${disabled ? "is-disabled" : ""}`}>
    <div className="cookie-consent-option-meta">
      <div className="cookie-consent-option-icon">{icon}</div>
      <div>
        <h4 className="cookie-consent-option-title">{title}</h4>
        <p className="cookie-consent-option-desc">{desc}</p>
      </div>
    </div>
    <button
      type="button"
      className={`cookie-consent-switch ${checked ? "is-active" : ""} ${disabled ? "is-disabled" : ""}`}
      onClick={onToggle}
      aria-checked={checked}
      role="switch"
      disabled={disabled}
      aria-label={`Toggle ${title} cookies`}
    >
      <span className="cookie-consent-switch-thumb" />
    </button>
  </div>
);

export default CookieConsent;